import { Injectable } from "@angular/core";
import {
  ApiId,
  IAppRecord,
  IOfflineAllDocs,
  IOfflineRecord,
  OFFLINE_QUEUE_MODEL,
} from "@app/models";
import PouchDB from "pouchdb";
import { defer, from, Observable, Subject, throwError } from "rxjs";
import { filter, map, takeUntil, tap } from "rxjs/operators";
import * as uuid from "uuid";

@Injectable({
  providedIn: "root",
})
export class PouchService {
  public pdb: any;
  protected scopeKey: string = null;
  private scopeGeneration = 0;
  private scopeChanged = new Subject<number>();

  constructor() {
    this.databaseCreate();
  }

  public slugId(model: string, id: ApiId) {
    return `${model}:${id}`;
  }

  public unslugId(id: ApiId) {
    const parts = id.toString().split(":");
    return parts[parts.length - 1];
  }

  public generatePouchId() {
    return uuid.v4();
  }

  private preparePouchId(values: any): ApiId {
    return values._id || values.id || this.generatePouchId();
  }

  private removeLegacyServerVersion<T extends Record<string, any>>(
    values: T
  ): T {
    if (values["server_version"] !== undefined) {
      delete values["_server_version"];
    }
    return values;
  }

  public databaseCreate() {
    this.pdb = new PouchDB(this.scopeKey || "dive_ops.db", {
      revs_limit: 1,
      auto_compaction: true,
    });
  }

  public activateScope(userId: ApiId, companyId: ApiId): void {
    if (!Number.isInteger(Number(userId)) || !Number.isInteger(Number(companyId))) {
      throw new Error("Local data scope requires numeric user and company IDs");
    }
    const scopeKey = `dive_ops.v2.u${userId}.c${companyId}.db`;
    if (this.scopeKey === scopeKey) return;
    const previous = this.pdb;
    this.scopeKey = scopeKey;
    this.scopeGeneration += 1;
    this.databaseCreate();
    this.scopeChanged.next(this.scopeGeneration);
    void previous?.close?.();
  }

  public deactivateScope(): void {
    const previous = this.pdb;
    this.scopeKey = null;
    this.scopeGeneration += 1;
    this.pdb = null;
    this.scopeChanged.next(this.scopeGeneration);
    void previous?.close?.();
  }

  public getScopeGeneration(): number {
    return this.scopeGeneration;
  }

  public onScopeChange(): Observable<number> {
    return this.scopeChanged.asObservable();
  }

  public assertScopeGeneration(generation: number): void {
    if (generation !== this.scopeGeneration || !this.scopeKey) {
      throw new Error("Local data scope changed during the operation");
    }
  }

  public bindToScope<T>(source: Observable<T>, generation = this.scopeGeneration): Observable<T> {
    return defer(() => {
      this.assertScopeGeneration(generation);
      return source.pipe(
        takeUntil(this.scopeChanged.pipe(filter((next) => next !== generation))),
        tap(() => this.assertScopeGeneration(generation))
      );
    });
  }

  public getScopePath(): string {
    if (!this.scopeKey) {
      throw new Error("Local data scope is not active");
    }
    return `scopes/${this.scopeKey.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
  }

  public databaseReset() {
    const pdb = this.pdb;
    const generation = this.scopeGeneration;
    return pdb.destroy().then(() => {
      this.assertScopeGeneration(generation);
      this.databaseCreate();
    });
  }

  public docId(model: string, id: ApiId) {
    return `${model}:${id}`;
  }

  public _create(values: IAppRecord): Observable<IOfflineRecord> {
    if (!values._id) {
      return throwError(() => new Error("ID is required to create a record."));
    }
    const pdb = this.pdb;
    return from(
      pdb.put(values).then(({ id }) => pdb.get(id))
    ) as Observable<IOfflineRecord>;
  }

  public create(model: string, values: any): Observable<IAppRecord> {
    return this._create({
      ...values,
      _id: this.slugId(model, values._id),
    }).pipe(
      map((doc) => {
        delete doc._rev;
        return { ...doc, _id: this.unslugId(doc._id) } as IAppRecord;
      })
    );
  }

  public _update(values: IAppRecord): Observable<IOfflineRecord> {
    if (!values._id) {
      return throwError(() => new Error("ID is required to update a record."));
    }
    const pdb = this.pdb;
    const createDocument = () => {
      const createdValues = this.removeLegacyServerVersion({ ...values });
      Object.keys(values).forEach((key) => {
        if (values[key] === undefined) {
          delete createdValues[key];
        }
      });
      return pdb.put(createdValues);
    };
    return from(
      pdb
        .get(values._id)
        .then(
          (doc: IOfflineRecord) => {
            const updatedValues = this.removeLegacyServerVersion({
              ...doc,
              ...values,
            });
            Object.keys(values).forEach((key) => {
              if (values[key] === undefined) {
                delete updatedValues[key];
              }
            });
            return pdb.put(updatedValues);
          },
          (error) => {
            if (error?.status !== 404) {
              throw error;
            }
            return createDocument();
          }
        )
        .then(() => pdb.get(values._id))
    ) as Observable<IOfflineRecord>;
  }

  public _modify(
    id: string,
    modifier: (current: IOfflineRecord) => Partial<IAppRecord>,
    initialValues?: IAppRecord,
    maxConflictRetries = 5
  ): Observable<IOfflineRecord> {
    const pdb = this.pdb;
    const updateLatestRevision = async (
      conflictCount = 0
    ): Promise<IOfflineRecord> => {
      let current: IOfflineRecord;
      try {
        current = (await pdb.get(id)) as IOfflineRecord;
      } catch (error) {
        if (error?.status !== 404 || !initialValues) {
          throw error;
        }
        current = { ...initialValues, _id: id } as IOfflineRecord;
      }

      try {
        const patch = modifier(current);
        const updatedValues = this.removeLegacyServerVersion({
          ...current,
          ...patch,
          _id: id,
        });
        Object.keys(patch).forEach((key) => {
          if (patch[key] === undefined) {
            delete updatedValues[key];
          }
        });
        if (!current._rev) {
          delete updatedValues._rev;
        }
        await pdb.put(updatedValues);
        return pdb.get(id);
      } catch (error) {
        if (error?.status === 409 && conflictCount < maxConflictRetries) {
          await new Promise((resolve) =>
            setTimeout(resolve, 10 * (conflictCount + 1))
          );
          return updateLatestRevision(conflictCount + 1);
        }
        throw error;
      }
    };

    return from(updateLatestRevision());
  }

  public _deleteIf(
    id: string,
    predicate: (current: IOfflineRecord) => boolean,
    maxConflictRetries = 5
  ): Observable<boolean> {
    const pdb = this.pdb;
    const deleteLatestRevision = async (
      conflictCount = 0
    ): Promise<boolean> => {
      const current = (await pdb.get(id)) as IOfflineRecord;
      if (!predicate(current)) {
        return false;
      }
      try {
        await pdb.remove(current);
        return true;
      } catch (error) {
        if (error?.status === 409 && conflictCount < maxConflictRetries) {
          await new Promise((resolve) =>
            setTimeout(resolve, 10 * (conflictCount + 1))
          );
          return deleteLatestRevision(conflictCount + 1);
        }
        throw error;
      }
    };

    return from(deleteLatestRevision());
  }

  public update(model: string, id: ApiId, values: any): Observable<IAppRecord> {
    return this._update({
      ...values,
      _id: this.slugId(model, id),
    }).pipe(
      map((updatedValues: IOfflineRecord) => {
        delete updatedValues._rev;
        return {
          ...updatedValues,
          _id: this.unslugId(updatedValues._id),
        } as IAppRecord;
      })
    );
  }

  public upsert(model: string, values: any): Observable<IAppRecord> {
    return this._update({
      ...values,
      _id: this.slugId(model, this.preparePouchId(values)),
    }).pipe(
      map((updatedValues: IOfflineRecord) => {
        delete updatedValues._rev;
        return {
          ...updatedValues,
          _id: this.unslugId(updatedValues._id),
        } as IAppRecord;
      })
    );
  }

  public _read(_id: string): Observable<IOfflineRecord> {
    const pdb = this.pdb;
    return from(
      pdb.get(_id, {
        revs: true,
        revs_info: true,
        conflicts: true,
      })
    ) as Observable<IOfflineRecord>;
  }

  public read(model: string, id: ApiId): Observable<IAppRecord> {
    const pdbId = this.slugId(model, id);
    return this._read(pdbId).pipe(
      map((doc: IOfflineRecord) => {
        delete doc._rev;
        return { ...doc, _id: id } as IAppRecord;
      })
    );
  }

  public _delete(_id: string): Observable<boolean> {
    const pdb = this.pdb;
    return from(
      pdb.get(_id).then((doc: any) => {
        return pdb.remove(doc);
      })
    ).pipe(map(() => true));
  }

  public delete(model: string, id: ApiId): Observable<boolean> {
    return this._delete(this.slugId(model, id));
  }

  public _search(model: string): Observable<IOfflineRecord[]> {
    const pdb = this.pdb;
    return from(
      pdb
        .allDocs({
          include_docs: true,
          startkey: `${model}:`,
          endkey: `${model}:\ufff0`,
        })
        .then((docs: IOfflineAllDocs) => docs.rows.map((row) => row.doc))
    ) as Observable<IOfflineRecord[]>;
  }

  public search(model: string): Observable<IAppRecord[]> {
    return this._search(model).pipe(
      map((data) =>
        data.map((row) => {
          delete row._rev;
          return { ...row, _id: this.unslugId(row._id) };
        })
      )
    );
  }

  public searchId(_id: string): Observable<number> {
    const pdb = this.pdb;
    return from(
      pdb.allDocs({ include_docs: true }).then((docs: IOfflineAllDocs) => {
        const rows = docs.rows
          .map((row) => row.doc)
          .filter((doc) => !doc._id.includes(OFFLINE_QUEUE_MODEL))
          .filter((doc) => doc._id.includes(_id));
        if (rows.length && rows[0]["id"]) {
          return rows[0]["id"] as number;
        } else {
          throw new Error(`"Offline ID ${_id} not found"`);
        }
      })
    ) as Observable<number>;
  }

  public bulkDocs(docs: any[]): Observable<any[]> {
    const pdb = this.pdb;
    return from(pdb.bulkDocs(docs)) as Observable<any[]>;
  }
}
